import type { ComponentProps, ComponentType, ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";

export interface RowActionsMenuProps {
  /** Accessible name naming the row, e.g. "Actions for Div 23 Mechanical". */
  label: string;
  children: ReactNode;
  disabled?: boolean;
  align?: ComponentProps<typeof DropdownMenuContent>["align"];
  size?: "sm" | "compact";
  className?: string;
  contentClassName?: string;
  /** Called when the menu opens or closes, e.g. to load item eligibility lazily. */
  onOpenChange?: (open: boolean) => void;
  /** Passed to the menu content, e.g. to keep focus on a field an item just revealed. */
  onCloseAutoFocus?: ComponentProps<typeof DropdownMenuContent>["onCloseAutoFocus"];
}

/**
 * The kebab (MoreHorizontal) menu at the end of a list/table row. Clicks on the trigger do not
 * bubble to row click handlers.
 */
export function RowActionsMenu({
  label,
  children,
  disabled,
  align = "end",
  size = "sm",
  className,
  contentClassName,
  onOpenChange,
  onCloseAutoFocus,
}: RowActionsMenuProps) {
  return (
    <DropdownMenu modal={false} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <IconButton
          label={label}
          icon={MoreHorizontal}
          size={size}
          tooltip={false}
          disabled={disabled}
          className={className}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={cn("min-w-40", contentClassName)}
        onCloseAutoFocus={onCloseAutoFocus}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface RowActionProps
  extends Omit<ComponentProps<typeof DropdownMenuItem>, "variant" | "onSelect" | "children"> {
  icon?: ComponentType<{ className?: string }>;
  onSelect: ComponentProps<typeof DropdownMenuItem>["onSelect"];
  /** Destructive styling (Delete, Remove). Pair destructive actions with a confirmation dialog. */
  destructive?: boolean;
  children: ReactNode;
}

export function RowAction({ icon: Icon, destructive = false, children, ...props }: RowActionProps) {
  return (
    <DropdownMenuItem variant={destructive ? "destructive" : "default"} {...props}>
      {Icon ? <Icon className="size-4" /> : null}
      {children}
    </DropdownMenuItem>
  );
}

export function RowActionSeparator(props: ComponentProps<typeof DropdownMenuSeparator>) {
  return <DropdownMenuSeparator {...props} />;
}
